import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, ArrowRight, MapPin, Clock, Euro, Rocket, ShieldCheck } from "lucide-react";
import { currencyForRegion } from "@/constants/data";

/**
 * Post-signup Instant Match screen — shows the new user their top 3 matches on the platform
 * so they see immediate value. Big dopamine hit; the alternative was a boring "Welcome" toast.
 */
export default function InstantMatches() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/matches/me")
      .then((r) => setData(r.data))
      .catch(() => setData({ role: user?.role, matches: [] }))
      .finally(() => setLoading(false));
  }, [user?.role]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center text-neutral-400 uppercase tracking-widest text-xs">
        Finding your matches…
      </div>
    );
  }

  const isEmployer = data?.role === "employer";
  const matches = data?.matches || [];
  const heroTitle = isEmployer ? "3 workers match your job" : "3 jobs match you";
  const heroSubtitle = isEmployer
    ? "Your first contact reveal is FREE. Ring them direct today — first contractor to call wins them."
    : "Apply now — first applicant usually gets the job.";

  return (
    <div data-testid="instant-matches-page" className="min-h-screen bg-neutral-950">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-[2px] w-10 bg-amber-500" />
          <span className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">
            You're on the board
          </span>
        </div>

        <h1 className="display text-4xl md:text-6xl">
          {matches.length > 0 ? (
            <>
              <span className="text-amber-500">{matches.length}</span>{" "}
              {isEmployer ? "workers" : "jobs"} match you right now.
            </>
          ) : (
            <>You're on. Now we watch for matches.</>
          )}
        </h1>

        {matches.length > 0 && (
          <p className="text-neutral-300 mt-4 max-w-lg text-lg">{heroSubtitle}</p>
        )}

        {isEmployer && (
          <div
            data-testid="free-first-badge"
            className="mt-6 inline-flex items-center gap-2 bg-amber-500/15 border border-amber-500/50 text-amber-500 px-4 py-2 text-xs uppercase tracking-widest font-bold"
          >
            <ShieldCheck className="w-4 h-4" />
            First contact reveal FREE · No card needed
          </div>
        )}

        {matches.length === 0 ? (
          <div className="mt-10 bg-neutral-900 border border-neutral-800 p-8 max-w-2xl">
            <Rocket className="w-8 h-8 text-amber-500 mb-3" />
            <div className="display text-2xl mb-2">You're first in the door.</div>
            <p className="text-neutral-400">
              No live matches this second — the platform's still filling up. The moment a
              {isEmployer ? " tradesperson" : " contractor"} that matches you joins, you'll get an
              email + they'll see your profile straight away.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/referrals"
                data-testid="cta-refer"
                className="inline-flex items-center gap-2 bg-amber-500 text-neutral-950 px-5 py-3 font-bold uppercase tracking-widest text-xs hover:bg-amber-400 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Earn €10 per friend you refer
              </Link>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 bg-neutral-900 border border-neutral-800 text-neutral-300 px-5 py-3 font-bold uppercase tracking-widest text-xs hover:border-amber-500 hover:text-amber-500 transition-colors"
              >
                Go to dashboard
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-10 grid gap-4">
            {matches.map((m) => (isEmployer ? <WorkerMatchCard key={m.id} w={m} /> : <JobMatchCard key={m.id} j={m} />))}
            <div className="mt-4">
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 text-sm uppercase tracking-widest font-bold"
                data-testid="cta-dashboard"
              >
                Go to dashboard <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function JobMatchCard({ j }) {
  const cur = currencyForRegion(j.county);
  return (
    <Link
      to={`/jobs/${j.id}`}
      data-testid={`match-job-${j.id}`}
      className="group bg-neutral-900 border border-neutral-800 hover:border-amber-500 p-6 transition-colors flex flex-col md:flex-row md:items-center gap-4"
    >
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-amber-500 font-bold mb-1.5">
          {j.trade}
          <span className="mx-2 text-neutral-700">·</span>
          <MapPin className="w-3 h-3 inline mb-0.5 mr-1" />
          {j.county}
        </div>
        <div className="display text-2xl mb-2">{j.title}</div>
        <div className="text-xs uppercase tracking-widest text-neutral-400 flex flex-wrap gap-3">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{j.duration}</span>
          {j.day_rate && <span className="flex items-center gap-1"><Euro className="w-3 h-3" />{cur}{j.day_rate}/day</span>}
          {j.company && <span>· {j.company}</span>}
        </div>
      </div>
      <ArrowRight className="w-5 h-5 text-neutral-600 group-hover:text-amber-500 group-hover:translate-x-1 transition-all" />
    </Link>
  );
}

function WorkerMatchCard({ w }) {
  const cur = currencyForRegion(w.county);
  return (
    <Link
      to={`/workers/${w.id}`}
      data-testid={`match-worker-${w.id}`}
      className="group bg-neutral-900 border border-neutral-800 hover:border-amber-500 p-6 transition-colors flex flex-col md:flex-row md:items-center gap-4"
    >
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-amber-500 font-bold mb-1.5">
          {w.trade}
          <span className="mx-2 text-neutral-700">·</span>
          <MapPin className="w-3 h-3 inline mb-0.5 mr-1" />
          {w.county}
          {w.counties && w.counties.length > 0 && <span className="text-amber-500 font-bold ml-1">+{w.counties.length}</span>}
        </div>
        <div className="display text-2xl mb-2">{w.name}</div>
        <div className="text-xs uppercase tracking-widest text-neutral-400 flex flex-wrap gap-3">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{w.availability}</span>
          {w.day_rate && <span className="flex items-center gap-1"><Euro className="w-3 h-3" />{cur}{w.day_rate}/day</span>}
          {w.years_experience ? <span>· {w.years_experience} yrs exp</span> : null}
        </div>
      </div>
      <div className="text-xs uppercase tracking-widest text-amber-500 font-bold text-right shrink-0">
        Reveal contact
        <div className="text-neutral-500 font-normal normal-case text-[10px] mt-0.5">First one free</div>
      </div>
      <ArrowRight className="w-5 h-5 text-neutral-600 group-hover:text-amber-500 group-hover:translate-x-1 transition-all" />
    </Link>
  );
}
